import { Field, ImageField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import StarIcon from '@mui/icons-material/Star';
import Tooltip from '@mui/material/Tooltip';
import NextImage from 'next/image';
import ButtonDiscreet from 'components/shared/Button/ButtonDiscreet';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import { NearbySiteSummary } from 'components/SiteMaster/types';
import { SITE_MASTER_COLORS } from 'components/SiteMaster/SiteMaster.styles';
import {
  StSavedSiteCard,
  StSavedSiteCardButtonWrap,
  StSavedSiteCardHeader,
  StSavedSiteCardLocationRow,
  StSavedSiteCardSiteName,
  StSavedSiteCardStarButton,
  StSavedSiteCardTag,
  StSavedSiteCardTagList,
} from './SavedSiteCarousel.styles';

const DEFAULT_VISIBLE_CONDITIONS = 2;
const DEFAULT_TAG_TOOLTIP_BG = '#1C252B';

const INLINE_DISCREET_BUTTON_STYLE = {
  minHeight: 'auto',
  height: 'auto',
  alignSelf: 'center',
  fontSize: '14px',
  fontWeight: 500,
  padding: '0 8px',
};

const tagTooltipComponentsProps = (tooltipBg: string) => ({
  tooltip: {
    sx: {
      bgcolor: tooltipBg,
      borderRadius: '12px',
      boxShadow: 3,
      padding: '8px 12px',
      fontSize: '14px',
      maxWidth: 'none',
    },
  },
  arrow: {
    sx: { color: tooltipBg },
  },
});

export interface SavedSiteCardFields {
  mapPin?: ImageField;
  viewDetailsButton?: Field<string>;
  favoriteButtonColor?: Field<string>;
  favoriteButtonToolTipBGColor?: Field<string>;
  unSaveButtonHoverTextLoggedIn?: Field<string>;
}

export interface SavedSiteCardProps {
  site: NearbySiteSummary;
  fields: SavedSiteCardFields;
  onUnsave: (site: NearbySiteSummary) => void;
  isUnsaving?: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
}

function SavedSiteConditionTags({
  conditions,
  tooltipBg = DEFAULT_TAG_TOOLTIP_BG,
  expanded,
  onToggle,
}: {
  conditions: string[];
  tooltipBg?: string;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const hiddenCount = Math.max(0, conditions.length - DEFAULT_VISIBLE_CONDITIONS);
  const visibleConditions = expanded ? conditions : conditions.slice(0, DEFAULT_VISIBLE_CONDITIONS);

  return (
    <StSavedSiteCardTagList>
      {visibleConditions.map((condition) => (
        <Tooltip
          key={condition}
          title={condition}
          arrow
          placement="top"
          componentsProps={tagTooltipComponentsProps(tooltipBg)}
        >
          <StSavedSiteCardTag>{condition}</StSavedSiteCardTag>
        </Tooltip>
      ))}
      {hiddenCount > 0 && (
        <ButtonDiscreet
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Show less' : `+${hiddenCount} more`}
          style={INLINE_DISCREET_BUTTON_STYLE}
          onClick={onToggle}
        >
          {expanded ? 'Show less' : `+${hiddenCount} more`}
        </ButtonDiscreet>
      )}
    </StSavedSiteCardTagList>
  );
}

export function formatSavedSiteLocationText(site: NearbySiteSummary): string {
  const location = [site.city, site.state].filter(Boolean).join(', ');
  const hasDistance = Number.isFinite(site.distanceMiles) && site.distanceMiles > 0;
  const distance = hasDistance ? `${site.distanceMiles.toFixed(1)} mi` : '';

  if (location && distance) {
    return `${location} • ${distance}`;
  }
  if (location) {
    return location;
  }
  return distance;
}

function SavedSiteCard({
  site,
  fields,
  onUnsave,
  isUnsaving,
  expanded,
  onToggleExpanded,
}: SavedSiteCardProps): JSX.Element {
  const pinSrc = fields.mapPin?.value?.src;
  const starColor = fields.favoriteButtonColor?.value || SITE_MASTER_COLORS.teal;
  const tooltipBg = fields.favoriteButtonToolTipBGColor?.value || DEFAULT_TAG_TOOLTIP_BG;
  const tooltipText =
    fields.unSaveButtonHoverTextLoggedIn?.value ||
    'Click again to remove this site from your saved sites.';
  const conditions = site.conditions ?? [];

  return (
    <StSavedSiteCard id={`saved-site-card-${site.id}`}>
      <Tooltip
        title={tooltipText}
        arrow
        placement="top"
        componentsProps={tagTooltipComponentsProps(tooltipBg)}
      >
        <StSavedSiteCardStarButton
          aria-label={tooltipText}
          disabled={isUnsaving}
          onClick={() => onUnsave(site)}
          sx={{ color: starColor }}
        >
          <StarIcon sx={{ width: 24, height: 24 }} />
        </StSavedSiteCardStarButton>
      </Tooltip>

      <StSavedSiteCardHeader>
        <StSavedSiteCardSiteName>{site.name}</StSavedSiteCardSiteName>
        <StSavedSiteCardLocationRow>
          {pinSrc && (
            <NextImage
              src={pinSrc}
              width={16}
              height={16}
              alt={(fields.mapPin?.value?.alt as string) || ''}
              aria-hidden
            />
          )}
          <span>{formatSavedSiteLocationText(site)}</span>
        </StSavedSiteCardLocationRow>
      </StSavedSiteCardHeader>

      {conditions.length > 0 && (
        <SavedSiteConditionTags
          conditions={conditions}
          tooltipBg={tooltipBg}
          expanded={expanded}
          onToggle={onToggleExpanded}
        />
      )}

      <StSavedSiteCardButtonWrap>
        <ButtonSecondary href={site.url || undefined} fullWidth disabled={!site.url}>
          <Text field={fields.viewDetailsButton} />
        </ButtonSecondary>
      </StSavedSiteCardButtonWrap>
    </StSavedSiteCard>
  );
}

export default SavedSiteCard;
